'use client';

import { useState, useEffect, useRef } from 'react';
import { onSnapshot } from 'firebase/firestore';
import { useUserSession } from '@/hooks/use-user-session';
import { useLocale } from 'next-intl';
import { useRouter } from 'next/navigation';
import { Bell } from 'lucide-react';
import { 
  markNotificationAsRead, 
  deleteNotification, 
  getNotificationsQuery 
} from '@/libs/firebase/notification-service';

export function NotificationBell() {
  const userId = useUserSession();
  const locale = useLocale();
  const router = useRouter();
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (userId === undefined || userId === null) {
      setUnreadCount(0);
      setNotifications([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    const q = getNotificationsQuery(userId);

    if (!q) {
      setIsLoading(false);
      return;
    }

    // Set up real-time listener for unread notifications
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const notifs = snapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            createdAt: data.createdAt?.toDate?.() || (data.createdAt?.toMillis ? new Date(data.createdAt.toMillis()) : new Date()),
          };
        });
        setNotifications(notifs);
        setUnreadCount(notifs.length);
        setIsLoading(false);
      },
      (error) => {
        console.error('Error fetching notifications:', error);
        setIsLoading(false);
      }
    );

    return () => unsubscribe();
  }, [userId]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const handleMarkAsRead = async (notificationId) => {
    if (!userId) return;
    try {
      await markNotificationAsRead(userId, notificationId);
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  const handleDelete = async (notificationId) => {
    if (!userId) return;
    try {
      await deleteNotification(userId, notificationId);
    } catch (error) {
      console.error('Failed to delete notification:', error);
    }
  };

  const handleActionClick = (actionUrl) => {
    if (actionUrl) {
      const localizedUrl = actionUrl.startsWith('/') ? `/${locale}${actionUrl}` : actionUrl;
      router.push(localizedUrl);
      setIsOpen(false);
    }
  };

  if (userId === undefined || userId === null) {
    return null;
  }

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Icon Button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="relative flex h-10 w-10 md:h-11 md:w-11 items-center justify-center rounded-full border border-[#6A3683]/50 bg-[#070219]/80 text-[#FBFBFE] shadow-[0_8px_18px_rgba(7,2,25,0.4)] hover:border-[#D4B6FF] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A45DE8]"
        aria-label="Notifications"
        aria-expanded={isOpen}
      >
        <Bell className="h-5 w-5" />
        
        {/* Unread Badge */}
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-bold leading-none text-white bg-red-600 rounded-full border-2 border-[#070219]">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 md:w-96 bg-[#050316]/95 backdrop-blur-sm rounded-2xl border border-[#6A3683]/40 shadow-[0_20px_45px_rgba(7,2,25,0.5)] z-50 overflow-hidden">
          {/* Header */}
          <div className="p-4 border-b border-[#6A3683]/30 flex justify-between items-center">
            <h3 className="text-sm font-semibold text-[#FBFBFE]" style={{ fontFamily: "var(--font-marcellus)" }}>
              Notifications {unreadCount > 0 && `(${unreadCount})`}
            </h3>
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="text-[#D4B6FF]/70 hover:text-[#FBFBFE] transition-colors"
              aria-label="Close notifications"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Notifications List */}
          {isLoading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-400 mx-auto mb-2" />
              <p className="text-sm text-gray-400">Loading notifications...</p>
            </div>
          ) : notifications.length === 0 ? (
            <div className="p-8 text-center text-sm text-gray-400">
              No new notifications
            </div>
          ) : (
            <div className="max-h-96 overflow-y-auto sidebar-scrollable">
              {notifications.map((notification) => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  onMarkAsRead={() => handleMarkAsRead(notification.id)}
                  onDelete={() => handleDelete(notification.id)}
                  onActionClick={() => handleActionClick(notification.actionUrl)}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function NotificationItem({ notification, onMarkAsRead, onDelete, onActionClick }) {
  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'error':
        return 'border-l-4 border-red-500 bg-red-900/20';
      case 'warning':
        return 'border-l-4 border-yellow-500 bg-yellow-900/20';
      case 'success':
        return 'border-l-4 border-green-500 bg-green-900/20';
      default:
        return 'border-l-4 border-[#A45DE8] bg-[#6A3683]/10';
    }
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'error':
        return '⚠️';
      case 'warning':
        return '⚡';
      case 'success':
        return '✅';
      default:
        return 'ℹ️';
    }
  };

  const formatTime = (date) => {
    if (!date || !(date instanceof Date)) {
      return 'Just now';
    }

    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div
      className={`p-4 border-b border-[#6A3683]/20 last:border-b-0 hover:bg-[#6A3683]/10 transition-colors ${getSeverityColor(
        notification.severity
      )}`}
    >
      <div className="flex justify-between items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-lg flex-shrink-0">{getSeverityIcon(notification.severity)}</span>
            <h4 className="text-sm font-semibold text-[#FBFBFE] truncate" style={{ fontFamily: "var(--font-marcellus)" }}>
              {notification.title}
            </h4>
          </div>
          <p className="text-sm text-gray-300 mt-1 leading-relaxed" style={{ fontFamily: "var(--font-inter)" }}>
            {notification.message}
          </p>

          {/* Action Link */}
          {notification.actionUrl && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onMarkAsRead();
                onActionClick();
              }}
              className="text-xs text-[#D4B6FF] hover:text-[#A45DE8] mt-2 inline-block font-medium transition-colors"
            >
              Take Action →
            </button>
          )}

          {/* Timestamp */}
          <p className="text-xs text-gray-400 mt-2">
            {formatTime(notification.createdAt)}
          </p>
        </div>

        {/* Close Button */}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          className="text-gray-400 hover:text-gray-300 flex-shrink-0 transition-colors"
          title="Dismiss"
          aria-label="Dismiss notification"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
}
